import type { TFunction } from "i18next";
import { addGroupMember, changeGroup, excludeGroup, includeGroup, readAccessList, removeGroupMember, reorderGroups, setGroupGrants } from "../../../api/access/accessApi";
import type { Grant, GrantOverride, Permission } from "../../../api/access/accessModel";
import { grantedHere, permissionsOf, switchesOnServerKeys } from "../model/grantSets";
import { restoredPosition } from "../model/groupOrder";
import type { AccessChange } from "./useAccessChanges";

/**
 * The changes the access screens make, each with what it says once done and its inverse for
 * Undo (redesign R3-2 "Saving"). The server checks each one again. Switching on the server's
 * keys needs a reason (GS-4): asked first where the screen knows the change does it, and
 * otherwise when the server answers that it does.
 */

interface Named {
  readonly groupId: string;
  /** As the screen names it. */
  readonly name: string;
}

/** An installation group as a project includes it: its own set, from the installation. */
interface Included extends Named {
  readonly grants: readonly Grant[];
}

type Change = Omit<AccessChange, "inverse">;

/** A change that never needs a reason; its title is never asked. */
function plain(done: string, apply: AccessChange["apply"]): Change {
  return { done, needsReason: false, reasonTitle: done, apply };
}

function withInverse(change: Change, inverse: Change | null): AccessChange {
  return { ...change, inverse: inverse === null ? null : { ...inverse, inverse: null } };
}

/** A group's whole set from `before` to `after`. */
export function grantsChange(t: TFunction, group: Named, before: readonly Grant[], after: readonly Grant[]): AccessChange {
  const make = (from: readonly Grant[], to: readonly Grant[]): Change => ({
    done: grantsDone(t, group.name, from, to),
    needsReason: switchesOnServerKeys(from, to),
    reasonTitle: t("access.reason.serverKeys", { group: group.name }),
    apply: (reason) => setGroupGrants(group.groupId, to, reason),
  });
  return withInverse(make(before, after), make(after, before));
}

/** What an included installation group switches on or off in a project; the reason follows what it gives here. */
export function overridesChange(t: TFunction, projectId: string, group: Included, before: readonly GrantOverride[], after: readonly GrantOverride[]): AccessChange {
  const make = (from: readonly GrantOverride[], to: readonly GrantOverride[]): Change => ({
    done: t("access.done.overrides", { group: group.name }),
    needsReason: switchesOnServerKeys(grantedHere(group.grants, from), grantedHere(group.grants, to)),
    reasonTitle: t("access.reason.serverKeysHere", { group: group.name }),
    apply: (reason) => includeGroup(projectId, group.groupId, to, reason),
  });
  return withInverse(make(before, after), make(after, before));
}

/**
 * Includes an installation group in the project, as the installation sets it. The project's
 * list doesn't show an includable group's own set, so the server says whether it brings the
 * keys and the reason is asked then.
 */
export function inclusionChange(t: TFunction, projectId: string, group: Named): AccessChange {
  return withInverse(
    { done: t("access.done.included", { group: group.name }), needsReason: false, reasonTitle: t("access.reason.serverKeysHere", { group: group.name }), apply: (reason) => includeGroup(projectId, group.groupId, [], reason) },
    plain(t("access.done.excluded", { group: group.name }), () => excludeGroup(projectId, group.groupId)),
  );
}

/**
 * Stops including an installation group. Undo includes it again with what it switched here,
 * and puts it back in its place in the list, since the place decides who manages whom.
 */
export function exclusionChange(t: TFunction, projectId: string, group: Included, overrides: readonly GrantOverride[], order: readonly string[]): AccessChange {
  return withInverse(plain(t("access.done.excluded", { group: group.name }), () => excludeGroup(projectId, group.groupId)), {
    done: t("access.done.included", { group: group.name }),
    needsReason: switchesOnServerKeys([], grantedHere(group.grants, overrides)),
    reasonTitle: t("access.reason.serverKeysHere", { group: group.name }),
    apply: async (reason) => {
      await includeGroup(projectId, group.groupId, overrides, reason);
      const current = await readAccessList(projectId);
      const ids = current.groups.map((listed) => listed.groupId);
      const restored = restoredPosition(ids, group.groupId, order);
      if (restored.some((id, index) => id !== ids[index])) await reorderGroups(projectId, restored, current.revision);
    },
  });
}

export function memberAdded(t: TFunction, group: Named, person: { readonly userId: string; readonly name: string }): AccessChange {
  return withInverse(
    plain(t("access.done.memberAdded", { name: person.name, group: group.name }), () => addGroupMember(group.groupId, person.userId)),
    plain(t("access.done.memberRemoved", { name: person.name, group: group.name }), () => removeGroupMember(group.groupId, person.userId)),
  );
}

export function memberRemoved(t: TFunction, group: Named, person: { readonly userId: string; readonly name: string }): AccessChange {
  return withInverse(
    plain(t("access.done.memberRemoved", { name: person.name, group: group.name }), () => removeGroupMember(group.groupId, person.userId)),
    plain(t("access.done.memberAdded", { name: person.name, group: group.name }), () => addGroupMember(group.groupId, person.userId)),
  );
}

/** A new order of the scope's list; Undo writes the old one against the revision this one made. */
export function orderChange(t: TFunction, projectId: string | null, moved: Named, before: readonly string[], after: readonly string[], revision: number): AccessChange {
  let revisionAfter = revision;
  const done = t("access.done.moved", { group: moved.name });
  return withInverse(
    plain(done, async () => { revisionAfter = await reorderGroups(projectId, after, revision); }),
    plain(done, async () => { await reorderGroups(projectId, before, revisionAfter); }),
  );
}

/** A new name, or for an installation group a new place inside another. */
export function groupChange(t: TFunction, group: Named & { readonly parentId: string | null }, name: string, parentId: string | null): AccessChange {
  return withInverse(
    plain(t("access.done.changed", { group: name }), () => changeGroup(group.groupId, name, parentId)),
    plain(t("access.done.changed", { group: group.name }), () => changeGroup(group.groupId, group.name, group.parentId)),
  );
}

/** "Contributors can now: Make and publish releases." for one permission switched; otherwise a general line. */
function grantsDone(t: TFunction, group: string, before: readonly Grant[], after: readonly Grant[]): string {
  const had = new Set(permissionsOf(before));
  const has = new Set(permissionsOf(after));
  const gained = [...has].filter((permission) => !had.has(permission));
  const lost = [...had].filter((permission) => !has.has(permission));
  const label = (permission: Permission) => t(`access.permissions.${permission}`);
  const [on] = gained;
  const [off] = lost;
  if (on !== undefined && gained.length === 1 && lost.length === 0) return t("access.done.grantOn", { group, permission: label(on) });
  if (off !== undefined && lost.length === 1 && gained.length === 0) return t("access.done.grantOff", { group, permission: label(off) });
  return t("access.done.grants", { group });
}
